<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: Check
  Reason: Two VISIBLY SEPARATE sections for the two person questions of a case — J1 „Aktualität" over
          the already linked rows, J2 „Zuordnung" over the unlinked ones. The separation is AUFLAGE A3
          and is carried by the two headings plus the delimitation sentence in J1.

  ⚠ AUFLAGE A3: the two headings below are the last line of defence against the mistake this feature
  has made three times — a J1 finding („ihr Stand ist alt") being read as a J2 verdict („kennen wir
  nicht"). Do not merge the sections, do not shorten a heading to „Prüfung", and do not move a J1 row
  into the J2 area. The two row sets are disjoint by construction; the labels say so out loud.
-->
<div class="cw-case-person-check flex flex-column gap-4">

    <!-- ==========================================================================================
         SECTION J1 „Aktualität" — the ALREADY LINKED rows.
         Its question: „Diese Person kennen wir — ist ihr erfasster Stand noch aktuell?"
         Display only: no write path, no matcher, no person matrix.
         ========================================================================================== -->
    <section class="cw-case-person-check-section flex flex-column gap-3">

        <div class="flex align-items-start justify-content-between gap-3 flex-wrap">

            <div class="flex align-items-start gap-2">
                <i class="fa-regular fa-clock-rotate-left text-primary mt-1"></i>
                <div class="flex flex-column gap-1">
                    <span class="text-lg font-medium">{{ currencyTitle }}</span>
                    <span class="text-sm text-color-secondary">{{ currencyQuestion }}</span>
                    <!-- AUFLAGE A5: what this check actually compares. Without it „Stand aktuell" reads
                         as „alle Angaben stimmen" — a claim about a birth date nobody compared. -->
                    <span class="text-sm text-color-secondary font-italic">{{ currencyScopeNote }}</span>
                </div>
            </div>

            @if (initialized) {
                <div class="flex align-items-center gap-2 flex-wrap">
                    <p-tag class="text-sm align-items-baseline"
                           [value]="summary.checked + ' geprüft'"
                           severity="secondary"></p-tag>
                    @if (summary.diverging > 0) {
                        <p-tag class="text-sm align-items-baseline"
                               icon="fa-regular fa-triangle-exclamation"
                               [value]="summary.diverging + ' abweichend'"
                               severity="warn"></p-tag>
                    }
                    @if (summary.withoutCapturedIdentity > 0) {
                        <p-tag class="text-sm align-items-baseline"
                               [value]="summary.withoutCapturedIdentity + ' ohne erfassten Stand'"
                               severity="secondary"></p-tag>
                    }
                    <!-- S16: its OWN chip next to the name counts, never merged into them — the two
                         dimensions are orthogonal and a row can be counted in both. -->
                    @if (summary.notBoundToCustomer > 0) {
                        <p-tag class="text-sm align-items-baseline"
                               icon="fa-regular fa-user-slash"
                               [value]="summary.notBoundToCustomer + ' ohne Kundenzuordnung'"
                               severity="warn"></p-tag>
                    }
                </div>
            }
        </div>

        @if (busy) {
            <div class="flex flex-column gap-2">
                <p-skeleton height="2rem"></p-skeleton>
                <p-skeleton height="2rem"></p-skeleton>
                <p-skeleton height="2rem"></p-skeleton>
            </div>
        }

        <!-- A FAILED read and an EMPTY result mean the opposite of one another — hence a state of its
             own, with a retry, rather than an empty list that would claim there is nothing to check. -->
        @if (!busy && loadFailed) {
            <div class="cw-case-person-check-error flex p-3 gap-3 justify-content-between align-items-center border-round-lg">
                <div class="flex gap-2 align-items-center">
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    <span class="text-sm">{{ loadFailedMessage }}</span>
                </div>
                <p-button label="Erneut versuchen" styleClass="w-max" icon="fa-solid fa-rotate"
                          severity="secondary" (onClick)="handleRetry()"></p-button>
            </div>
        }

        @if (!busy && !loadFailed && initialized) {

            @if (checkResults.length === 0) {
                <div class="flex align-items-center gap-2 p-3 surface-100 border-round-lg">
                    <i class="fa-regular fa-circle-info text-color-secondary"></i>
                    <span class="text-sm text-color-secondary">
                        In diesem Vorgang ist keine Person mit einer Person im System verknüpft. Für
                        diesen Abschnitt gibt es deshalb nichts zu prüfen.
                    </span>
                </div>
            } @else {

                <!-- AUFLAGE A3: the sentence that stops a J1 finding from being read as a J2 „kennen
                     wir nicht". Deliberately ABOVE the list, not in a footnote — it has to be read
                     before the findings, not after them. -->
                <div class="flex align-items-start gap-2 p-3 surface-100 border-round-lg">
                    <i class="fa-regular fa-circle-info text-primary mt-1"></i>
                    <span class="text-sm text-color-secondary">{{ currencyDelimitation }}</span>
                </div>

                <!-- AUFLAGE A6: a FAILED matrix read is stated out loud, with a retry. Silence here
                     would make a customer-foreign binding look unremarkable, which is the exact reason
                     S16 was reinstated. The wording is the reserved constant, verbatim. -->
                @if (customerBindingFailed) {
                    <div class="flex p-3 gap-3 justify-content-between align-items-center surface-100 border-round-lg">
                        <div class="flex gap-2 align-items-center">
                            <i class="fa-regular fa-triangle-exclamation text-color-secondary"></i>
                            <span class="text-sm text-color-secondary">{{ bindingNotVerifiableMessage }}</span>
                        </div>
                        <p-button label="Erneut prüfen" styleClass="w-max" icon="fa-solid fa-rotate"
                                  severity="secondary" (onClick)="handleBindingRetry()"></p-button>
                    </div>
                }

                <p-table [value]="checkResults"
                         [rows]="20"
                         [paginator]="checkResults.length > 20"
                         [rowHover]="true"
                         styleClass="p-datatable-sm">

                    <ng-template pTemplate="header">
                        <tr>
                            <th class="w-14rem">Status</th>
                            <th>Im Vorgang erfasst</th>
                            <th>Verknüpfte Person (aktuell)</th>
                            <!-- S16: its OWN column, and only when the question was actually asked.
                                 An always-present column would have to say something about a check
                                 that never ran — and „zugeordnet" is exactly the claim a skipped
                                 check may not make. -->
                            @if (customerBindingChecked) {
                                <th class="w-12rem">{{ bindingColumnTitle }}</th>
                            }
                            <th>Hinweis</th>
                        </tr>
                    </ng-template>

                    <ng-template pTemplate="body" let-result>
                        <tr>
                            <td>
                                @if (result.status === 'DIVERGING') {
                                    <p-tag class="text-sm align-items-baseline"
                                           icon="fa-regular fa-triangle-exclamation"
                                           [value]="statusLabel(result)"
                                           severity="warn"></p-tag>
                                } @else if (result.status === 'MATCHING') {
                                    <p-tag class="text-sm align-items-baseline"
                                           icon="fa-regular fa-circle-check"
                                           [value]="statusLabel(result)"
                                           severity="success"></p-tag>
                                } @else {
                                    <p-tag class="text-sm align-items-baseline"
                                           [value]="statusLabel(result)"
                                           severity="secondary"></p-tag>
                                }
                            </td>
                            <td><span class="text-sm">{{ capturedLabel(result) }}</span></td>
                            <td><span class="text-sm">{{ personLabel(result) }}</span></td>

                            <!-- S16, ORTHOGONAL to the Status column on the left: a row can read
                                 „Stand aktuell" here and „Nicht zugeordnet" there, and BOTH have to be
                                 legible at once. That is why this is a column of its own and not a
                                 fourth value of the status tag. -->
                            @if (customerBindingChecked) {
                                <td>
                                    @if (result.customerBinding === 'NOT_BOUND_TO_CUSTOMER') {
                                        <p-tag class="text-sm align-items-baseline"
                                               icon="fa-regular fa-user-slash"
                                               [value]="bindingLabel(result)"
                                               severity="warn"></p-tag>
                                    } @else if (result.customerBinding === 'NOT_VERIFIABLE') {
                                        <p-tag class="text-sm align-items-baseline"
                                               icon="fa-regular fa-circle-question"
                                               [value]="bindingLabel(result)"
                                               severity="secondary"></p-tag>
                                    } @else {
                                        <p-tag class="text-sm align-items-baseline"
                                               icon="fa-regular fa-user-check"
                                               [value]="bindingLabel(result)"
                                               severity="success"></p-tag>
                                    }
                                </td>
                            }

                            <td>
                                <!-- BOTH statements, each verbatim from its own constant
                                     (CW_CASE_PERSON_CHECK_DIVERGING_MESSAGE and the S16 pair) and
                                     never concatenated into one sentence: a row that carries both a
                                     stale name and a foreign binding must show both, one under the
                                     other, with neither hiding the other. -->
                                <div class="flex flex-column gap-1">
                                    @if (result.message) {
                                        <span class="text-sm text-color-secondary">{{ result.message }}</span>
                                    }
                                    @if (result.customerBindingMessage) {
                                        <span class="text-sm text-color-secondary">{{ result.customerBindingMessage }}</span>
                                    }
                                </div>
                            </td>
                        </tr>
                    </ng-template>
                </p-table>
            }
        }
    </section>

    <!-- ==========================================================================================
         SECTION J2 „Zuordnung" — the NOT YET LINKED rows.
         Its question: „Wer ist dieser Mensch?" — a SEARCH, answered by cr-customer-persons.

         Guard 4: the WHOLE section hangs off @if (customerId) — heading, counters and surface alike.
         Without a customer there is no stock to search in, and a heading that poses a question nothing
         on screen could answer is the A3 confusion in a different costume: an empty area under a live
         heading reads as „nichts gefunden". Nothing is requested in that state either.
         ========================================================================================== -->
    @if (customerId; as embeddedCustomerId) {

        <p-divider></p-divider>

        <section class="cw-case-person-check-section flex flex-column gap-3">

            <div class="flex align-items-start justify-content-between gap-3 flex-wrap">

                <div class="flex align-items-start gap-2">
                    <i class="fa-regular fa-magnifying-glass text-primary mt-1"></i>
                    <div class="flex flex-column gap-1">
                        <span class="text-lg font-medium">{{ assignmentTitle }}</span>
                        <span class="text-sm text-color-secondary">{{ assignmentQuestion }}</span>
                    </div>
                </div>

                @if (initialized) {
                    <div class="flex align-items-center gap-2 flex-wrap">
                        <p-tag class="text-sm align-items-baseline"
                               [value]="suggestions.length + ' offen'"
                               severity="secondary"></p-tag>
                        <!-- Guard 2/3: a plain yes/no statement about WHETHER a comparison happened.
                             Deliberately NO number derived from the match results — with a name-only
                             delivery „new" means „we delivered too little to recognise her", not
                             „she does not exist", and a count would invite the duplicate creation
                             this whole feature prevents. -->
                        @if (assignmentComparisonRan) {
                            <p-tag class="text-sm align-items-baseline"
                                   icon="fa-regular fa-circle-check"
                                   value="Abgleich durchgeführt"
                                   severity="secondary"></p-tag>
                        }
                    </div>
                }
            </div>

            @if (initialized && suggestions.length === 0) {
                <div class="flex align-items-center gap-2 p-3 surface-100 border-round-lg">
                    <i class="fa-regular fa-circle-check text-color-secondary"></i>
                    <span class="text-sm text-color-secondary">
                        Alle Personen dieses Vorgangs sind bereits verknüpft. Für diesen Abschnitt gibt
                        es nichts zuzuordnen.
                    </span>
                </div>
            }

            <!--
              Guard 1: ONE element whose key CHANGES on a case or customer switch. `track surfaceKey`
              therefore destroys the old instance and creates a fresh one, rather than merely re-binding
              [customerId] — a surviving instance reaches session-wide, shared state, which is the class
              of defect this feature keeps producing. Do NOT replace this with a bare @if: when
              casePersons arrives through the @Input there is no gap between false and true, so the
              template would never observe the false state and the instance would survive.
            -->
            @for (surfaceKey of embeddedSurfaceKeys; track surfaceKey) {
                <!--
                  `initialized` is deliberately NOT omitted here: the embedded surface must mount with
                  the FINISHED delivery, not first with an empty one and immediately afterwards with the
                  real one — that would be an extra match run that tells nobody anything. While the read
                  is in flight, the first section's loading indicator does the talking.
                -->
                @if (initialized) {
                    <cr-customer-persons
                            [customerId]="embeddedCustomerId"
                            [suggestions]="suggestions"
                            [header]="assignmentEmbedHeader"
                            [allowCreate]="false"
                            [readonly]="readonly"
                            (onMatchResultChange)="handleMatchResultChange($event)"
                            (onDiscrepancyResolved)="handleDiscrepancyResolved($event)">
                    </cr-customer-persons>
                }
            }
        </section>

    } @else {
        <div class="flex align-items-center gap-2 p-3 surface-100 border-round-lg">
            <i class="fa-regular fa-circle-info text-color-secondary"></i>
            <span class="text-sm text-color-secondary">
                Dem Vorgang ist kein Kunde zugeordnet. Ohne Kunden gibt es keinen Bestand, in dem nach
                diesen Personen gesucht werden könnte — der zweite Abschnitt wird deshalb nicht
                angezeigt.
            </span>
        </div>
    }
</div>
